import React from 'react';
import { Phone, MapPin, Mail, Instagram, UtensilsCrossed, ShoppingCart, Clock } from 'lucide-react';
import { TenantData } from './publicMenuTypes';
import { Table } from '../../services/dataService';

type Props = {
  tenantData: TenantData;
  isTableMode: boolean;
  tableData: Table | null;
  customerName: string;
  cartCount: number;
  cartTotal: number;
  activeOrderCount: number;
  onOpenCart: () => void;
  onOpenOrderStatus: () => void;
};

const PublicMenuHeader: React.FC<Props> = ({
  tenantData,
  isTableMode,
  tableData,
  customerName,
  cartCount,
  cartTotal,
  activeOrderCount,
  onOpenCart,
  onOpenOrderStatus,
}) => {
  return (
    <header className="border-b border-zinc-200/80 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-5xl flex-col gap-2 px-3 py-3 tablet:px-4 tablet:py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-white shadow-sm">
                <UtensilsCrossed className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-base font-bold leading-tight text-zinc-900 tablet:text-lg">
                  {tenantData.businessName}
                </h1>
                <p className="text-[11px] text-zinc-500 tablet:text-xs">Menu</p>
              </div>
            </div>
          </div>

          {isTableMode && tableData && (
            <div className="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                onClick={onOpenCart}
                className="relative rounded-full bg-zinc-100 p-2 text-zinc-800 transition hover:bg-zinc-200 active:scale-95"
                aria-label="Open cart"
              >
                <ShoppingCart className="h-5 w-5" aria-hidden />
                {cartCount > 0 && (
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-amber-500 px-0.5 text-[10px] font-bold text-white">
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>
              {activeOrderCount > 0 && (
                <button
                  type="button"
                  onClick={onOpenOrderStatus}
                  className="relative rounded-full bg-zinc-100 p-2 text-zinc-800 transition hover:bg-zinc-200 active:scale-95"
                  aria-label="Order status"
                >
                  <Clock className="h-5 w-5" aria-hidden />
                  <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-blue-500 px-0.5 text-[10px] font-bold text-white">
                    {activeOrderCount}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        {isTableMode && tableData && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-emerald-50 px-2.5 py-1.5 text-[11px] text-emerald-900 tablet:text-xs">
            <span className="font-semibold">
              Table {tableData.tableNumber}
              {tableData.name ? ` · ${tableData.name}` : ''}
            </span>
            {customerName && (
              <>
                <span className="hidden text-emerald-700/60 sm:inline">|</span>
                <span className="truncate">{customerName}</span>
              </>
            )}
            {cartCount > 0 && (
              <>
                <span className="text-emerald-700/60">·</span>
                <span className="font-medium">₹{cartTotal.toFixed(2)}</span>
              </>
            )}
          </div>
        )}

        {(tenantData.phone || tenantData.address || tenantData.email || tenantData.instagramHandle) && (
          <div className="flex flex-wrap items-center justify-center gap-2 border-t border-zinc-100 pt-2 tablet:justify-start">
            {tenantData.phone && (
              <a
                href={`tel:${tenantData.phone}`}
                className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-1 text-[10px] font-medium text-zinc-700 hover:bg-zinc-200 tablet:text-xs"
              >
                <Phone className="h-3 w-3 shrink-0 text-emerald-600" />
                <span className="max-w-[140px] truncate tablet:max-w-none">{tenantData.phone}</span>
              </a>
            )}
            {tenantData.address && (
              <span
                className="inline-flex max-w-[200px] items-center gap-1 truncate rounded-full bg-zinc-100 px-2 py-1 text-[10px] text-zinc-600 tablet:max-w-xs tablet:text-xs"
                title={tenantData.address}
              >
                <MapPin className="h-3 w-3 shrink-0 text-red-500" />
                {tenantData.address}
              </span>
            )}
            {tenantData.email && (
              <a
                href={`mailto:${tenantData.email}`}
                className="hidden items-center gap-1 rounded-full bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-200 xs:inline-flex"
              >
                <Mail className="h-3 w-3 text-blue-600" />
                Email
              </a>
            )}
            {tenantData.instagramHandle && (
              <a
                href={`https://instagram.com/${tenantData.instagramHandle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2 py-1 text-[10px] font-medium text-zinc-700 hover:bg-zinc-200 tablet:text-xs"
              >
                <Instagram className="h-3 w-3 text-pink-600" />@{tenantData.instagramHandle}
              </a>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default PublicMenuHeader;
